import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, useLoadScript, Libraries } from '@react-google-maps/api';
import { geocodeAddress } from '../utils/geocoding';
import { Location } from '../types';

interface LocationMapProps {
  postcode?: string;
  streetNumber?: string;
  onConfirm: (location: Location) => void;
}

// Define libraries as a static constant
const libraries: Libraries = ['places', 'geocoding'];

// Define load script options as a static constant
const loadScriptOptions = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  libraries,
  version: "beta",
  language: 'en',
  region: 'GB',
  additionalOptions: {
    importLibrary: ['marker']
  }
};

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  marginBottom: '16px'  // Add space below map
};

const center = {
  lat: 51.5074,
  lng: -0.1278
};

export const LocationMap: React.FC<LocationMapProps> = ({ postcode, streetNumber, onConfirm }) => {
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markerModuleLoaded, setMarkerModuleLoaded] = useState(false);
  
  const { isLoaded, loadError } = useLoadScript(loadScriptOptions);

  useEffect(() => {
    if (isLoaded && postcode && streetNumber) {
      geocodeAddress(`${postcode} ${streetNumber}`)
        .then(location => {
          if (location) {
            setMarkerPosition(location);
          }
        })
        .catch(error => console.error('Geocoding error:', error));
    }
  }, [isLoaded, postcode, streetNumber]);

  useEffect(() => {
    if (isLoaded) {
      console.log('Script loaded');
      console.log('Checking marker availability:');
      console.log('- google.maps:', !!google.maps);
      console.log('- marker module:', !!google.maps.marker);
      console.log('- AdvancedMarkerElement:', !!google.maps.marker?.AdvancedMarkerElement);
      
      if (google.maps && !google.maps.marker) {
        console.log('Attempting to load marker module...');
        google.maps.importLibrary('marker')
          .then(() => {
            console.log('Marker module loaded successfully');
            console.log('AdvancedMarkerElement now available:', !!google.maps.marker?.AdvancedMarkerElement);
          })
          .catch(error => console.error('Failed to load marker module:', error));
      }
    }
  }, [isLoaded]);

  // Load marker module
  useEffect(() => {
    if (isLoaded && google.maps && !markerModuleLoaded) {
      google.maps.importLibrary('marker')
        .then(() => {
          console.log('Marker module loaded successfully');
          setMarkerModuleLoaded(true);
        })
        .catch(error => console.error('Failed to load marker module:', error));
    }
  }, [isLoaded, markerModuleLoaded]);

  // Create marker effect
  useEffect(() => {
    if (map && markerPosition && markerModuleLoaded) {
      // Remove previous marker if it exists
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: markerPosition,
        title: "Drag to adjust location",
        gmpDraggable: true
      });

      marker.addListener('dragend', () => {
        const position = marker.position as google.maps.LatLng;
        if (position) {
          setMarkerPosition({
            lat: position.lat(),
            lng: position.lng()
          });
        }
      });

      return () => {
        marker.map = null;
      };
    }
  }, [map, markerPosition, markerModuleLoaded]);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };
      console.log('Map clicked at:', newPosition);
      setMarkerPosition(newPosition);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (markerPosition) {
      onConfirm(markerPosition);
    }
  }, [markerPosition, onConfirm]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    console.log('Map loaded');
    console.log('google.maps available:', !!google.maps);
    console.log('marker module available:', !!google.maps.marker);
    console.log('AdvancedMarkerElement available:', !!google.maps.marker?.AdvancedMarkerElement);
    setMap(map);
  }, []);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <div>
      <div style={{ width: '100%', height: '400px', borderRadius: '12px', marginBottom: '16px' }}>
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: '100%',
            borderRadius: '12px'
          }}
          zoom={17}
          center={markerPosition || center}
          onClick={handleMapClick}
          onLoad={onMapLoad}
          options={{
            mapId: 'e28f67038331ddbe',
            mapTypeId: 'hybrid'
          }}
        />
        <button 
          onClick={handleConfirm}
          disabled={!markerPosition}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            left: '24px',
            padding: '8px 16px',
            backgroundColor: '#0052FF',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            zIndex: 10,
            width: 'calc(100% - 48px)'
          }}
        >
          Confirm Location
        </button>
      </div>
    </div>
  );
}; 